import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib'
import { Loading } from '../../app/ui'
import { Err, PartnerPage, SetupFooter } from './common'

// CHANGE-045: the "Earn rewards | ernd" button for the partner's own site. Done once they copy the code.

type Theme = 'light' | 'dark'
type Button = { go_url: string; width: number; height: number; copied: boolean } & Record<Theme, { src: string; code: string }>

const STAGE: Record<Theme, string> = { light: '#F4F6F9', dark: '#11100E' }
const TITLE: Record<Theme, string> = { light: 'Light', dark: 'Dark' }

/** Clipboard first; on an http page or a refused permission, the old select-and-copy. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0'
    document.body.appendChild(ta)
    ta.select()
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    ta.remove()
    return ok
  }
}

export function ButtonPage() {
  return <PartnerPage>{(pid) => <ButtonBody pid={pid} />}</PartnerPage>
}

function ButtonBody({ pid }: { pid: string }) {
  const q = useQuery({ queryKey: ['partners', 'button', pid], queryFn: () => api<Button>('GET', `/api/partners/${pid}/button`) })
  if (q.error) return <Err error={q.error} />
  if (!q.data) return <Loading />
  return (
    <div className="page narrow stack" data-testid="partners-button-page">
      <h1>Website button</h1>
      <p>Put it on your pricing page, or in your footer.</p>
      {(['light', 'dark'] as Theme[]).map((t) => (
        <Preview key={t} pid={pid} theme={t} b={q.data} />
      ))}
      <SetupFooter pid={pid} step="website_button" />
    </div>
  )
}

function Preview({ pid, theme, b }: { pid: string; theme: Theme; b: Button }) {
  const qc = useQueryClient()
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const copy = async () => {
    setState((await copyText(b[theme].code)) ? 'copied' : 'failed')
    // The step is done on the first copy. A refused save (staff viewing as the partner) changes nothing here.
    api('POST', `/api/partners/${pid}/button/copied`).then(() => qc.invalidateQueries({ queryKey: ['partners'] }), () => {})
  }
  return (
    <section className="panel stack" data-testid={`partners-button-${theme}`}>
      <h2>{TITLE[theme]}</h2>
      <div style={{ background: STAGE[theme], borderRadius: 8, padding: '32px 16px', display: 'flex', justifyContent: 'center' }}>
        <img src={b[theme].src} width={b.width} height={b.height} alt="Earn rewards with ernd" style={{ maxWidth: '100%', height: 'auto' }} />
      </div>
      <div className="row">
        <button type="button" className="primary" data-testid={`partners-button-copy-${theme}`} onClick={copy}>
          {state === 'copied' ? 'Copied' : 'Copy code'}
        </button>
      </div>
      {state === 'failed' && (
        <textarea readOnly rows={4} value={b[theme].code} data-testid={`partners-button-code-${theme}`}
          ref={(el) => el?.select()} style={{ fontFamily: 'var(--e-mono, monospace)', fontSize: 13 }} />
      )}
    </section>
  )
}
